/**
 * TaskUpdateChangeCard - Card for task update entity changes.
 *
 * Displays update content, type, visibility, and attribution.
 * Supports a condensed mode for embedding inside TaskChangeCard.
 */

import { Activity, ArrowRightLeft, Bot, MessageSquare, SkipForward, User } from 'lucide-react';

import { TaskLink } from '@/components/entity-links/entities';
import { Badge } from '@/components/ui/catalyst/badge';
import type {
  EntityChangeSchema,
} from '@/services/api/generated/entityChangesService';

import { useChangedFields } from '../utils/useChangedFields';
import { ChangedFieldHighlight } from './ChangedFieldHighlight';
import { useEntityChangeActions } from './EntityChangeActionsContext';
import { EntityChangeCardShell } from './EntityChangeCardShell';
import { type FieldConfig, KeyFieldsGrid, KeyFieldsGridUnchanged } from './KeyFieldsGrid';
import { ShowMoreSection } from './ShowMoreSection';

function getUserDisplayName(user: Record<string, unknown> | undefined): string {
  if (!user) return 'Unknown';
  const first = user.first_name as string | undefined;
  const last = user.last_name as string | undefined;
  if (first || last) return [first, last].filter(Boolean).join(' ');
  return (user.username as string) || 'Unknown';
}

const KEY_FIELDS: FieldConfig[] = [
  { key: 'update_type', label: 'Type', isEnum: true },
  { key: 'visibility', label: 'Visibility' },
  { key: 'created_at', label: 'Created', isDate: true },
];

const REFERENCE_FIELDS: FieldConfig[] = [
  { key: 'correlation_id', label: 'Correlation ID' },
];

interface TaskUpdateChangeCardProps {
  entity: EntityChangeSchema;
  condensed?: boolean;
  matterId?: string;
}

export const TaskUpdateChangeCard: React.FC<TaskUpdateChangeCardProps> = ({
  entity,
  condensed,
  matterId,
}) => {
  const changes = useChangedFields(
    entity.change_type,
    entity.changed_fields ?? [],
  );
  const actions = useEntityChangeActions();
  const effectiveStatus =
    actions?.statusOverrides[entity.id as string] ?? entity.status;

  const snapshot =
    entity.change_type === 'deleted' ? entity.old_snapshot : entity.new_snapshot;
  const data = (snapshot?.data ?? {}) as Record<string, unknown>;
  const updateText = (data.update as string) || '';
  const updateType = (data.update_type as string) || 'comment';
  const visibility = (data.visibility as string) || '';
  const user = data.user as Record<string, unknown> | undefined;
  const agent = data.agent as string | undefined;
  const agentTags = (data.agent_tags as string[]) ?? [];

  const IconComponent = updateType === 'activity' ? Activity : MessageSquare;
  const snippet = updateText;
  const hasChanges = changes.totalChanges > 0;

  const showCondensedActions = Boolean(
    condensed && actions && effectiveStatus === 'pending',
  );

  // Condensed mode: compact row for embedding in TaskChangeCard
  if (condensed) {
    return (
      <div
        className={`flex items-start gap-2 rounded-md px-3 py-2 ${
          hasChanges ? 'bg-amber-50/40' : 'bg-gray-50/50'
        }`}
      >
        <IconComponent className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
            {snippet}
          </p>
          <div className="flex items-center gap-1.5 mt-0.5">
            {user && (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <User className="h-3 w-3" />
                {getUserDisplayName(user)}
              </span>
            )}
            {agent && (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <Bot className="h-3 w-3" />
                {agent}
              </span>
            )}
            <Badge color="zinc" className="text-xs">
              {updateType}
            </Badge>
            {visibility && (
              <Badge
                color={visibility === 'Public' ? 'blue' : 'zinc'}
                className="text-xs"
              >
                {visibility}
              </Badge>
            )}
          </div>
        </div>
        {/* Status badge or action buttons */}
        <div className="shrink-0 flex items-center gap-1">
          {effectiveStatus && effectiveStatus !== 'pending' && (
            <Badge
              color={effectiveStatus === 'transferred' ? 'green' : 'amber'}
              className="text-xs"
            >
              {effectiveStatus === 'transferred' ? 'Transferred' : 'Skipped'}
            </Badge>
          )}
          {showCondensedActions && actions && (
            <div className="flex items-center gap-0.5 rounded-md border border-gray-200 bg-white overflow-hidden">
              <button
                type="button"
                onClick={() =>
                  actions.onStatusChange(entity.id as string, 'transferred')
                }
                className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                <ArrowRightLeft className="h-3 w-3 text-gray-400" />
                Transferred
              </button>
              <div className="w-px h-3.5 bg-gray-200" />
              <button
                type="button"
                onClick={() =>
                  actions.onStatusChange(entity.id as string, 'skipped')
                }
                className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                <SkipForward className="h-3 w-3 text-gray-400" />
                Skip
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <EntityChangeCardShell
      changeType={entity.change_type}
      name={snippet || 'Task Update'}
      icon={IconComponent}
      entityStatus={effectiveStatus}
      onStatusChange={
        actions
          ? (newStatus) => actions.onStatusChange(entity.id as string, newStatus)
          : undefined
      }
      iconBgClassName="bg-slate-50"
      iconTextClassName="text-slate-600"
      badges={
        <>
          <Badge color="zinc" className="text-xs">
            {updateType}
          </Badge>
          {visibility && (
            <Badge
              color={visibility === 'Public' ? 'blue' : 'zinc'}
              className="text-xs"
            >
              {visibility}
            </Badge>
          )}
        </>
      }
      changeCount={changes.totalChanges}
      hasExpandedContent
    >
      {/* Update content */}
      {changes.isFieldChanged('update') ? (
        <ChangedFieldHighlight
          label="Update"
          fieldKey="update"
          changes={changes}
        >
          <p className="text-sm text-gray-900 whitespace-pre-wrap">
            {updateText}
          </p>
        </ChangedFieldHighlight>
      ) : (
        <div className="px-1 py-0.5">
          <span className="text-xs text-gray-400">Update:</span>
          <p className="text-sm text-gray-700 whitespace-pre-wrap mt-0.5">
            {updateText}
          </p>
        </div>
      )}

      {/* Attribution */}
      <div className="flex items-center gap-2 px-1 py-0.5">
        {user && (
          <span className="flex items-center gap-1 text-xs text-gray-500">
            <User className="h-3 w-3" />
            {getUserDisplayName(user)}
          </span>
        )}
        {agent && (
          <span className="flex items-center gap-1 text-xs text-gray-500">
            <Bot className="h-3 w-3" />
            {agent}
          </span>
        )}
        {agentTags.map((tag) => (
          <Badge key={tag} color="zinc" className="text-xs">
            {tag}
          </Badge>
        ))}
      </div>

      <KeyFieldsGrid
        fields={KEY_FIELDS}
        data={data}
        changes={changes}
        changeType={entity.change_type}
      />

      {/* Task link */}
      {matterId && typeof data.task_id === 'string' && data.task_id && (
        <div className="flex items-baseline gap-1.5 px-1 py-0.5">
          <span className="text-xs text-gray-400 shrink-0">Task:</span>
          <TaskLink entityId={data.task_id} matterId={matterId} className="text-sm font-normal">
            Task
          </TaskLink>
        </div>
      )}

      <ShowMoreSection changeType={entity.change_type}>
        <KeyFieldsGridUnchanged
          fields={KEY_FIELDS}
          data={data}
          changes={changes}
          changeType={entity.change_type}
        />
        <KeyFieldsGrid
          fields={REFERENCE_FIELDS}
          data={data}
          changes={changes}
          changeType={entity.change_type}
        />
        <KeyFieldsGridUnchanged
          fields={REFERENCE_FIELDS}
          data={data}
          changes={changes}
          changeType={entity.change_type}
        />
      </ShowMoreSection>
    </EntityChangeCardShell>
  );
};
